/**
 * Dictarea din fereastra „Send feedback" (FeedbackModal în main.tsx).
 *
 * Recunoașterea vocală a BROWSERULUI (Web Speech API), ca în panoul
 * configuratorului din widget (sdk/src/speech.ts) și în proiectul Grants:
 * textul apare în căsuță cât vorbești și se poate corecta înainte de trimitere.
 * La noi ajunge doar textul trimis; sunetul îl procesează furnizorul browserului
 * (Google în Chrome, Apple în Safari) — privacy §2.1. Firefox nu are API-ul,
 * deci acolo microfonul nu apare.
 *
 * Peste Grants: limba se alege (nu e fixată pe ro-RO — clienții vorbesc altele),
 * iar dictarea nu se oprește la prima pauză (Chrome închide sesiunea după câteva
 * secunde de liniște; o repornim până omul apasă Stop, cu plafon anti-buclă).
 */

export const DICTATION_LANGS = [
  { code: "en-US", label: "English" },
  { code: "ro-RO", label: "Română" },
  { code: "es-ES", label: "Español" },
  { code: "de-DE", label: "Deutsch" },
  { code: "fr-FR", label: "Français" },
  { code: "it-IT", label: "Italiano" },
  { code: "pt-PT", label: "Português" },
] as const;

/** Prima limbă a browserului pe care o avem în listă (după prefix: "ro" → "ro-RO"); altfel engleza. */
export function defaultDictationLang(browserLangs: readonly string[]): string {
  for (const lang of browserLangs) {
    const prefix = lang.toLowerCase().split("-")[0];
    const hit = DICTATION_LANGS.find((l) => l.code.toLowerCase().startsWith(`${prefix}-`));
    if (hit) return hit.code;
  }
  return "en-US";
}

/** Lipește ce s-a dictat după textul existent; majusculă la început de propoziție. */
export function joinDictation(base: string, said: string): string {
  const head = base.replace(/\s+$/, "");
  let tail = said.trim();
  if (!tail) return head;
  if (!head || /[.!?]$/.test(head)) tail = tail.charAt(0).toUpperCase() + tail.slice(1);
  return head ? `${head} ${tail}` : tail;
}

/** Ruta din consolă, fără query: contextul atașat feedbackului (nu trimitem parametri). */
export function feedbackScreen(hash: string): string {
  return (hash || "#/").split("?")[0].slice(0, 200);
}

/** Erori după care NU mai repornim: omul trebuie să facă ceva (permisiune, rețea, alt browser). */
export type DictationProblem = "blocked" | "network" | "no-mic" | "failed";

function problemOf(error: string): DictationProblem | null {
  if (error === "no-speech" || error === "aborted") return null; // liniște / oprit de noi: nu e o problemă
  if (error === "not-allowed" || error === "service-not-allowed") return "blocked";
  if (error === "network") return "network";
  if (error === "audio-capture") return "no-mic";
  return "failed";
}

interface RecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: { results: { length: number; [i: number]: { 0: { transcript: string } } } }) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}
type RecognitionCtor = new () => RecognitionLike;

function recognitionCtor(): RecognitionCtor | null {
  if (typeof window === "undefined" || !window.isSecureContext) return null;
  const w = window as unknown as Record<string, unknown>;
  return (w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null) as RecognitionCtor | null;
}

export function dictationSupported(): boolean {
  return recognitionCtor() !== null;
}

// 60 de reporniri ≈ câteva minute de vorbit cu pauze; peste, ceva e stricat.
const MAX_RESTARTS = 60;

export interface DictationOptions {
  lang: string;
  /** textul din căsuță la începutul fiecărei sesiuni — dictarea continuă după el */
  getBase: () => string;
  onText: (text: string) => void;
  onListening: (listening: boolean) => void;
  onProblem: (problem: DictationProblem) => void;
}

export interface Dictation {
  start(): void;
  stop(): void;
}

export function createDictation(opts: DictationOptions): Dictation | null {
  const Ctor = recognitionCtor();
  if (!Ctor) return null;
  const rec = new Ctor();
  rec.lang = opts.lang;
  rec.continuous = true;
  rec.interimResults = true;

  let wanted = false;
  let restarts = 0;
  let problem: DictationProblem | null = null;
  let base = "";

  const begin = () => {
    base = opts.getBase();
    try {
      rec.start();
    } catch {
      wanted = false;
      opts.onListening(false);
      opts.onProblem("failed");
    }
  };

  rec.onresult = (e) => {
    // Reconstruit din TOATE rezultatele la fiecare eveniment, niciodată acumulat:
    // Chrome de Android retrimite tot ce s-a spus, iar `+=` dubla textul
    // (lecția din sdk/src/speech.ts, 1-6 aug 2026).
    let said = "";
    for (let i = 0; i < e.results.length; i++) said += e.results[i][0].transcript;
    opts.onText(joinDictation(base, said));
  };
  rec.onerror = (e) => {
    const p = problemOf(e.error);
    if (p) problem = p;
  };
  rec.onend = () => {
    if (wanted && !problem && restarts < MAX_RESTARTS) {
      restarts += 1;
      begin();
      return;
    }
    wanted = false;
    opts.onListening(false);
    if (problem) opts.onProblem(problem);
  };

  return {
    start() {
      if (wanted) return;
      wanted = true;
      restarts = 0;
      problem = null;
      opts.onListening(true);
      begin();
    },
    stop() {
      if (!wanted) return;
      wanted = false;
      rec.stop();
    },
  };
}
